import { create } from 'zustand';
import {
  AppErrorCode,
  ClientEvent,
  type JoinQueuePayload,
  MatchWave,
  MediaKind,
  MessageStatus,
  METRIKA_GOALS,
  MessageType,
  QUOTE_PREVIEW_MAX_LENGTH,
  type ChatDto,
  type ChatWithMessagesDto,
  type MessageDto,
  type MessageQuoteDto,
  type FriendRequestDto,
  type FriendshipDto,
  type QueuePositionPayload,
  type ReportReason,
  type SavedChatDto,
} from '@anon-chat/shared';
import { maybeShowAd } from '@/shared/adsgram';
import { api } from '@/shared/api';
import { reachGoal } from '@/shared/metrika';
import { useAdsStore } from '@/store/ads.store';
import { emitWithAck } from '@/shared/socket';
import { webLoginPending } from '@/shared/web-login-ticket';

export type FlowScreen = 'onboarding' | 'search' | 'chat' | 'saved';

interface SearchState {
  active: boolean;
  position: number;
  startedAt: number | null;
  wave: MatchWave;
}

interface ChatState {
  screen: FlowScreen;
  search: SearchState;
  chat: ChatDto | null;
  messages: MessageDto[];
  partnerTyping: boolean;
  partnerOnline: boolean;
  /**
   * Собеседник не просто ушёл, а входит через Telegram. Отдельно от
   * `partnerOnline`: «не в сети» и «сейчас вернётся» — разные новости, и от
   * второй не жмут «Следующий».
   */
  partnerAuthAway: boolean;
  closedReason: string | null;
  lastError: string | null;
  /** Собственное соединение с сервером: обрыв нужно показать, а не скрыть. */
  connected: boolean;
  /** Диалог завершил собеседник, а не пользователь. */
  partnerLeft: boolean;
  /**
   * Жалоба отправлена и ждёт, чтобы человеку об этом сказали.
   *
   * Держится здесь, а не на экране диалога: диалог после жалобы закрывается, и
   * окно исчезло бы вместе с ним — ровно в тот момент, когда человек ждёт
   * подтверждения, что его услышали.
   */
  reportSent: boolean;
  /** Сообщение, на которое готовится ответ. */
  replyTo: MessageDto | null;

  /** Сохранённые диалоги: список раздела «Мои чаты». */
  savedChats: SavedChatDto[];
  /** Открытый сейчас диалог сохранённый, а не случайный. */
  isFriendChat: boolean;
  /** Предложение дружбы, ждущее ответа. */
  incomingRequest: FriendRequestDto | null;
  /** Дружба только что состоялась: показывается строкой в переписке. */
  friendshipJustMade: boolean;
  /** Наше предложение отправлено и ждёт ответа. */
  friendRequestSent: boolean;
  /**
   * Чем ответили на наше предложение. Отправитель об этом не узнавал вовсе:
   * согласие меняло только ленту, а отказ не менял ничего — и окно
   * «предложение отправлено» висело поверх обоих исходов.
   */
  friendOutcome: 'accepted' | 'declined' | 'already' | null;

  // Команды
  startSearch: (payload: JoinQueuePayload) => Promise<boolean>;
  cancelSearch: () => Promise<void>;
  sendText: (text: string) => Promise<void>;
  /** Загружает файл и отправляет его: превью появляется в чате сразу. */
  sendPhotoFile: (file: File) => Promise<void>;
  /** Отправляет записанное голосовое сообщение. */
  sendVoice: (blob: Blob, durationSec: number, waveform: string) => Promise<void>;
  startReply: (message: MessageDto) => void;
  cancelReply: () => void;
  report: (reason: ReportReason, comment?: string) => Promise<boolean>;
  leaveChat: () => Promise<void>;
  markRead: (messageId: string) => void;
  setTyping: (isTyping: boolean) => void;

  /** Предложить дружбу. Возвращает код отказа или null, если всё вышло. */
  requestFriendship: () => Promise<string | null>;
  /** Ответить на предложение. */
  respondToRequest: (accept: boolean, requestId?: string) => Promise<void>;
  /** Догружает ждущее предложение: после перезагрузки вкладки его нет в памяти. */
  loadPendingRequest: (chatId: string) => Promise<void>;
  loadSavedChats: () => Promise<void>;
  openSavedChats: () => Promise<void>;
  openSavedChat: (chatId: string) => Promise<void>;
  removeFriend: (chatId: string) => Promise<void>;

  // Реакции на события сервера
  onMatchFound: (chat: ChatDto) => void;
  onQueuePosition: (payload: QueuePositionPayload) => void;
  onIncomingMessage: (message: MessageDto) => void;
  onReadReceipt: (messageId: string) => void;
  onPartnerTyping: (isTyping: boolean) => void;
  onPartnerStatus: (chatId: string, isOnline: boolean, authAway?: boolean) => void;
  onChatClosed: (reason: string, requeued: boolean) => void;
  onPartnerLeft: () => void;
  dismissPartnerLeft: () => void;
  dismissReportSent: () => void;
  onConnectionChange: (connected: boolean) => void;
  onQueueLeft: () => void;
  onFriendRequested: (request: FriendRequestDto) => void;
  onFriendAccepted: (payload: FriendshipDto) => void;
  onFriendDeclined: (chatId: string) => void;
  /** Отвечать было не на что: связь между этими двумя уже есть. */
  onFriendAlready: (chatId: string) => void;
  /** Исход прочитан человеком: окно закрыто. */
  clearFriendOutcome: () => void;
  onFriendRemoved: (chatId: string) => void;
  restoreChat: (chat: ChatDto, messages: MessageDto[]) => void;
  /** Сверка с сервером после возвращения связи. */
  syncWithServer: () => Promise<void>;
  goToOnboarding: () => void;
  setError: (message: string | null) => void;
}

const idleSearch: SearchState = {
  active: false,
  position: 0,
  startedAt: null,
  wave: MatchWave.STRICT,
};

/** Локальный id оптимистичного сообщения. */
const clientId = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `c${Date.now()}${Math.random()}`;

/**
 * Цитата для оптимистичного сообщения. Строится по тем же правилам, что и на
 * сервере: ответ должен выглядеть одинаково до и после подтверждения.
 */
const quoteOf = (message: MessageDto): MessageQuoteDto => ({
  id: message.id,
  type: message.type,
  text:
    message.type === MessageType.PHOTO
      ? null
      : (message.text?.slice(0, QUOTE_PREVIEW_MAX_LENGTH) ?? null),
  isMine: message.isMine,
});

export const useChatStore = create<ChatState>((set, get) => ({
  screen: 'onboarding',
  search: idleSearch,
  chat: null,
  messages: [],
  partnerTyping: false,
  partnerOnline: false,
  partnerAuthAway: false,
  closedReason: null,
  lastError: null,
  connected: false,
  partnerLeft: false,
  reportSent: false,
  replyTo: null,
  savedChats: [],
  isFriendChat: false,
  incomingRequest: null,
  friendshipJustMade: false,
  friendRequestSent: false,
  friendOutcome: null,

  async startSearch(payload) {
    // Перед поиском: человек уже нажал кнопку и готов ждать, поэтому пауза
    // здесь воспринимается спокойнее, чем посреди разговора.
    await maybeShowAd('searchStart');
    useAdsStore.getState().showInterstitial('searchStart');

    set({
      screen: 'search',
      search: { active: true, position: 0, startedAt: Date.now(), wave: MatchWave.STRICT },
      closedReason: null,
      lastError: null,
      partnerLeft: false,
    });

    const ack = await emitWithAck<QueuePositionPayload>(ClientEvent.JOIN_QUEUE, payload);
    if (!ack.ok) {
      set({ screen: 'onboarding', search: idleSearch, lastError: ack.error.code });
      return false;
    }

    // Цель засчитывается по принятой заявке, а не по нажатию: отказ сервера
    // конверсией не является.
    reachGoal(METRIKA_GOALS.SEARCH_STARTED);

    // Совпадение могло произойти мгновенно — тогда match_found уже перевёл
    // экран в чат, и позицию перетирать не нужно.
    if (get().screen === 'search') {
      set((state) => ({ search: { ...state.search, position: ack.data.position } }));
    }
    return true;
  },

  async cancelSearch() {
    await emitWithAck(ClientEvent.CANCEL_QUEUE);
    set({ screen: 'onboarding', search: idleSearch });
  },

  async sendText(text) {
    const chat = get().chat;
    const trimmed = text.trim();
    if (!chat || !trimmed) {
      return;
    }

    const replyTo = get().replyTo;
    const clientMessageId = clientId();
    const optimistic: MessageDto = {
      id: clientMessageId,
      chatId: chat.id,
      type: MessageType.TEXT,
      text: trimmed,
      media: null,
      isMine: true,
      status: MessageStatus.SENDING,
      createdAt: new Date().toISOString(),
      readAt: null,
      replyTo: replyTo ? quoteOf(replyTo) : null,
      clientMessageId,
    };

    // Первое своё сообщение в диалоге — та точка, после которой разговор
    // действительно состоялся.
    if (!get().messages.some((message) => message.isMine)) {
      reachGoal(METRIKA_GOALS.FIRST_MESSAGE);
    }

    set((state) => ({ messages: [...state.messages, optimistic], replyTo: null }));

    const ack = await emitWithAck<MessageDto>(ClientEvent.MESSAGE, {
      chatId: chat.id,
      text: trimmed,
      clientMessageId,
      ...(replyTo ? { replyToId: replyTo.id } : {}),
    });

    set((state) => ({
      messages: state.messages.map((message) =>
        message.id === clientMessageId
          ? ack.ok
            ? ack.data
            : { ...message, status: MessageStatus.FAILED }
          : message,
      ),
      lastError: ack.ok ? state.lastError : ack.error.code,
    }));
  },

  /**
   * Отправка фотографии.
   *
   * Превью попадает в переписку до загрузки: файл с телефона весит мегабайты,
   * и несколько секунд пустого экрана человек воспринимает как «не нажалось».
   * Локальная ссылка живёт до ответа сервера, потом заменяется настоящей —
   * иначе изображение пропало бы при следующем открытии диалога.
   */
  async sendPhotoFile(file) {
    const chat = get().chat;
    if (!chat) {
      return;
    }

    const replyTo = get().replyTo;
    const clientMessageId = clientId();
    const previewUrl = URL.createObjectURL(file);
    const optimistic: MessageDto = {
      id: clientMessageId,
      chatId: chat.id,
      type: MessageType.PHOTO,
      text: null,
      media: {
        id: clientMessageId,
        kind: MediaKind.PHOTO,
        url: previewUrl,
        mimeType: file.type,
        size: file.size,
        width: null,
        height: null,
        durationSec: null,
        waveform: null,
        expiresAt: new Date().toISOString(),
      },
      isMine: true,
      status: MessageStatus.SENDING,
      createdAt: new Date().toISOString(),
      readAt: null,
      replyTo: replyTo ? quoteOf(replyTo) : null,
      clientMessageId,
    };

    set((state) => ({ messages: [...state.messages, optimistic], replyTo: null }));

    const markFailed = () =>
      set((state) => ({
        messages: state.messages.map((message) =>
          message.id === clientMessageId
            ? { ...message, status: MessageStatus.FAILED }
            : message,
        ),
      }));

    let uploaded: { media: { id: string; url: string } };
    try {
      uploaded = await api.uploadMedia(file, chat.id, { filename: file.name });
    } catch (error) {
      markFailed();
      throw error;
    }

    const ack = await emitWithAck<MessageDto>(ClientEvent.MEDIA, {
      chatId: chat.id,
      mediaId: uploaded.media.id,
      clientMessageId,
      ...(replyTo ? { replyToId: replyTo.id } : {}),
    });

    if (!ack.ok) {
      markFailed();
      set({ lastError: ack.error.code });
      return;
    }

    set((state) => ({
      messages: state.messages.map((message) =>
        message.id === clientMessageId ? ack.data : message,
      ),
    }));

    // Локальная ссылка больше не нужна: в сообщении стоит серверная. При
    // неудаче превью остаётся, поэтому освобождается только здесь.
    URL.revokeObjectURL(previewUrl);
  },

  /**
   * Пока сообщение не подтверждено сервером, его id — локальный, и сервер
   * такую цитату отверг бы. Ответить на него можно после доставки.
   */
  startReply(message) {
    if (message.status === MessageStatus.SENDING || message.status === MessageStatus.FAILED) {
      return;
    }
    set({ replyTo: message });
  },

  cancelReply() {
    set({ replyTo: null });
  },

  /**
   * Отправка голосового.
   *
   * Пузырь появляется сразу и до конца загрузки показывает длительность:
   * запись на пятнадцать минут весит мегабайты, и на медленной сети ожидание
   * без следа на экране человек принимает за несработавшую кнопку.
   */
  async sendVoice(blob, durationSec, waveform) {
    const chat = get().chat;
    if (!chat) {
      return;
    }

    const replyTo = get().replyTo;
    const clientMessageId = clientId();
    const optimistic: MessageDto = {
      id: clientMessageId,
      chatId: chat.id,
      type: MessageType.VOICE,
      text: null,
      media: {
        id: clientMessageId,
        kind: MediaKind.VOICE,
        // Локальная ссылка: до ответа сервера играть можно уже её.
        url: URL.createObjectURL(blob),
        mimeType: blob.type,
        size: blob.size,
        width: null,
        height: null,
        durationSec,
        waveform,
        expiresAt: new Date().toISOString(),
      },
      isMine: true,
      status: MessageStatus.SENDING,
      createdAt: new Date().toISOString(),
      readAt: null,
      replyTo: replyTo ? quoteOf(replyTo) : null,
      clientMessageId,
    };

    set((state) => ({ messages: [...state.messages, optimistic], replyTo: null }));

    const markFailed = () =>
      set((state) => ({
        messages: state.messages.map((message) =>
          message.id === clientMessageId ? { ...message, status: MessageStatus.FAILED } : message,
        ),
      }));

    let uploaded: { media: { id: string; url: string } };
    try {
      uploaded = await api.uploadMedia(blob, chat.id, {
        kind: MediaKind.VOICE,
        durationSec,
        waveform,
        filename: 'voice',
      });
    } catch (error) {
      markFailed();
      throw error;
    }

    const ack = await emitWithAck<MessageDto>(ClientEvent.MEDIA, {
      chatId: chat.id,
      mediaId: uploaded.media.id,
      clientMessageId,
      ...(replyTo ? { replyToId: replyTo.id } : {}),
    });

    if (!ack.ok) {
      markFailed();
      set({ lastError: ack.error.code });
      return;
    }

    set((state) => ({
      messages: state.messages.map((message) =>
        message.id === clientMessageId ? ack.data : message,
      ),
    }));
  },

  async report(reason, comment) {
    const chat = get().chat;
    if (!chat) {
      return false;
    }

    const ack = await emitWithAck<{ reportId: string }>(ClientEvent.REPORT, {
      chatId: chat.id,
      reason,
      ...(comment ? { comment } : {}),
    });

    if (!ack.ok) {
      set({ lastError: ack.error.code });
      return false;
    }

    set({ reportSent: true });
    return true;
  },

  async leaveChat() {
    const chat = get().chat;
    if (!chat) {
      return;
    }
    await emitWithAck(ClientEvent.LEAVE_CHAT, { chatId: chat.id });
    set({ screen: 'onboarding', chat: null, messages: [], partnerTyping: false, replyTo: null });

    /*
      Реклама показывается здесь, а не по событию закрытия с сервера.

      Во-первых, так это нажатие пользователя — Adsgram прямо рекомендует
      вызывать показ из обработчика клика. Во-вторых, закрытие приходит и когда
      уходит собеседник: объявление перекрыло бы сообщение «Собеседник вышел из
      чата», ради которого мы это сообщение и заводили.
    */
    void maybeShowAd('chatEnd');
    useAdsStore.getState().showInterstitial('chatEnd');
  },

  markRead(messageId) {
    const chat = get().chat;
    if (chat) {
      void emitWithAck(ClientEvent.READ, { chatId: chat.id, messageId });
    }
  },

  setTyping(isTyping) {
    const chat = get().chat;
    if (chat) {
      void emitWithAck(ClientEvent.TYPING, { chatId: chat.id, isTyping });
    }
  },

  onMatchFound(chat) {
    reachGoal(METRIKA_GOALS.CHAT_MATCHED);
    set({
      partnerLeft: false,
      screen: 'chat',
      chat,
      messages: [],
      search: idleSearch,
      partnerTyping: false,
      partnerOnline: chat.partner.isOnline,
      partnerAuthAway: false,
      closedReason: null,
      replyTo: null,
      // Новый разговор начинается с чистого листа: ожидание ответа и исход
      // остались в предыдущем и к этому собеседнику отношения не имеют.
      isFriendChat: false,
      friendshipJustMade: false,
      friendRequestSent: false,
      friendOutcome: null,
      incomingRequest: null,
    });
  },

  onQueuePosition(payload) {
    set((state) => ({
      search: {
        ...state.search,
        active: true,
        position: payload.position,
        wave: payload.wave,
        startedAt: state.search.startedAt ?? Date.now() - payload.waitingMs,
      },
    }));
  },

  onIncomingMessage(message) {
    /*
      Сокет подписан на все сохранённые диалоги, поэтому сообщение может прийти
      из чата, который сейчас не открыт. Класть его в текущую переписку нельзя —
      реплика друга появилась бы посреди разговора со случайным собеседником.
      Вместо этого растёт счётчик непрочитанных: точка на значке «Моих чатов»
      должна загораться сразу, а не при следующем заходе в раздел.
    */
    const openChatId = get().chat?.id;

    if (!openChatId || message.chatId !== openChatId) {
      set((state) => ({
        savedChats: state.savedChats.map((saved) =>
          saved.chatId === message.chatId
            ? {
                ...saved,
                unreadCount: saved.unreadCount + 1,
                lastMessage: message.text,
                lastMessageAt: message.createdAt,
              }
            : saved,
        ),
      }));

      // Диалога ещё нет в списке — например, дружба состоялась в этот же миг.
      if (!get().savedChats.some((saved) => saved.chatId === message.chatId)) {
        void get().loadSavedChats();
      }
      return;
    }

    set((state) => ({
      messages: state.messages.some((item) => item.id === message.id)
        ? state.messages
        : [...state.messages, message],
      partnerTyping: false,
    }));
  },

  onReadReceipt(messageId) {
    set((state) => {
      const target = state.messages.find((message) => message.id === messageId);
      if (!target) {
        return state;
      }
      // Прочитанным считается всё до указанного сообщения включительно.
      return {
        messages: state.messages.map((message) =>
          message.isMine && message.createdAt <= target.createdAt
            ? { ...message, status: MessageStatus.READ }
            : message,
        ),
      };
    });
  },

  onPartnerTyping(isTyping) {
    set({ partnerTyping: isTyping });
  },

  onPartnerStatus(chatId, isOnline, authAway = false) {
    // Сокет получает статусы по всем сохранённым диалогам сразу: чужой не
    // должен переписывать состояние открытого.
    if (get().chat?.id !== chatId) {
      return;
    }
    // Возвращение снимает отметку об уходе само: пришедший в сеть человек уже
    // никуда не входит.
    set({ partnerOnline: isOnline, partnerAuthAway: isOnline ? false : authAway });
  },

  onChatClosed(reason, requeued) {
    set((state) => ({
      // При «Следующий» инициатор уже переведён в поиск — экран не сбрасываем.
      screen: requeued ? 'search' : 'onboarding',
      chat: null,
      messages: [],
      partnerTyping: false,
      replyTo: null,
      closedReason: reason,
      search: requeued
        ? { active: true, position: 0, startedAt: Date.now(), wave: MatchWave.STRICT }
        : state.search,
    }));
  },

  onPartnerLeft() {
    set({ partnerLeft: true });
  },

  dismissPartnerLeft() {
    set({ partnerLeft: false });
  },

  dismissReportSent() {
    set({ reportSent: false });
  },

  onConnectionChange(connected) {
    set({ connected });
  },

  onQueueLeft() {
    set({ search: idleSearch, screen: 'onboarding' });
  },

  /**
   * Сверка состояния диалога с сервером.
   *
   * Пока связи не было, диалог могли закрыть: собеседник ушёл или истёк
   * льготный период. Событие о закрытии до отключённого клиента не дошло и уже
   * не дойдёт — он так и остался бы в чате, которого нет, и упирался бы в
   * неотправляемые сообщения. Поэтому при каждом восстановлении связи
   * состояние берётся у сервера, а не из памяти вкладки.
   */
  async syncWithServer() {
    const current = get().chat;

    let active: ChatWithMessagesDto | null = null;
    try {
      active = await api.activeChat();
    } catch {
      // Сеть снова отвалилась: следующее подключение повторит сверку.
      return;
    }

    if (active) {
      if (active.id !== current?.id) {
        get().restoreChat(active, active.messages);
      }
      return;
    }

    if (!current) {
      return;
    }

    /*
      Сохранённый диалог сверять не с чем: он не бывает «активным» — из него
      уходят с экрана, а не из разговора, и сервер про него в этом ответе
      молчит по определению. Раньше любая сверка — возвращение из фона,
      разблокированный телефон — выбрасывала человека из такого диалога с
      сообщением, что собеседник его покинул.
    */
    if (get().isFriendChat) {
      return;
    }

    /*
      Идёт вход через бота — вывод откладываем.

      Вернувшийся из Telegram успевает спросить «где мой диалог?» ещё старым
      гостевым токеном, а разговор к этому моменту уже переехал на учётную
      запись: сервер честно отвечает «никакого», и человек получал «собеседник
      вышел из чата» ровно в момент возвращения. Через секунду сессия
      сменится, и та же сверка ответит правду.
    */
    if (webLoginPending()) {
      return;
    }

    // Диалог закрыт, пока нас не было. Показываем то же, что увидел бы
    // оставшийся в сети: разговор завершил собеседник.
    set({
      screen: 'onboarding',
      chat: null,
      messages: [],
      partnerTyping: false,
      partnerLeft: true,
      replyTo: null,
    });
  },

  restoreChat(chat, messages) {
    set({
      screen: 'chat',
      chat,
      messages,
      partnerOnline: chat.partner.isOnline,
      partnerAuthAway: false,
      search: idleSearch,
      replyTo: null,
      // Разговор на месте — значит, никто из него не выходил. Иначе поверх
      // возвращённого диалога висело бы «собеседник вышел из чата».
      partnerLeft: false,
    });
  },

  async requestFriendship() {
    const chat = get().chat;
    if (!chat) {
      return 'CHAT_NOT_FOUND';
    }

    const ack = await emitWithAck<FriendRequestDto>(ClientEvent.FRIEND_REQUEST, {
      chatId: chat.id,
    });

    // Ожидание ответа держится в store, а не на экране: вкладку могут
    // перезагрузить, а исход всё равно должен найти того, кто его ждал.
    if (ack.ok) {
      set({ friendRequestSent: true });
    }

    // Код отказа отдаётся экрану: у каждой причины свой разговор с человеком,
    // а общая красная строка внизу тут ничего не объясняет.
    return ack.ok ? null : ack.error.code;
  },

  async respondToRequest(accept: boolean, requestId?: string) {
    /*
      Идентификатор можно передать явно: вернувшийся из Telegram человек мог
      застать перезагруженную вкладку, где предложения в памяти уже нет, — а
      принять он его всё равно вправе.
    */
    const id = requestId ?? get().incomingRequest?.id;
    if (!id) {
      return;
    }
    set({ incomingRequest: null });

    const ack = await emitWithAck<{ accepted: boolean }>(ClientEvent.FRIEND_RESPOND, {
      requestId: id,
      accept,
    });

    if (!ack.ok) {
      /*
        «Вы уже друзья» — не ошибка, а ответ по существу: так бывает, когда
        предложение отправили гостю, а вошёл он под записью, с которой дружба
        уже есть. Человеку нужно сказать, где искать этот диалог, а не красную
        строку внизу экрана.
      */
      if (ack.error.code === AppErrorCode.ALREADY_FRIENDS) {
        set({ friendOutcome: 'already' });
        return;
      }
      set({ lastError: ack.error.code });
    }
  },

  async loadPendingRequest(chatId: string) {
    try {
      const request = await api.pendingFriendRequest(chatId);
      // Пока ответ летел, человек мог уйти в другой диалог.
      if (request && get().chat?.id === chatId) {
        set({ incomingRequest: request });
      }
    } catch {
      // Без предложения экран работает как обычно: молчание лучше ошибки.
    }
  },

  async loadSavedChats() {
    try {
      set({ savedChats: await api.savedChats() });
    } catch {
      // Список не загрузился — экран покажет пустоту, а не ошибку: человек
      // всегда может вернуться в поиск.
    }
  },

  async openSavedChats() {
    set({ screen: 'saved' });
    await get().loadSavedChats();
  },

  async openSavedChat(chatId: string) {
    const chat = await api.chatById(chatId);
    if (!chat) {
      await get().loadSavedChats();
      return;
    }

    set((state) => ({
      // Счётчик обнуляется сразу: человек уже смотрит на эти сообщения, и
      // ждать ответа сервера об отметке прочтения незачем.
      savedChats: state.savedChats.map((saved) =>
        saved.chatId === chatId ? { ...saved, unreadCount: 0 } : saved,
      ),
      screen: 'chat',
      chat,
      messages: chat.messages,
      isFriendChat: true,
      friendshipJustMade: false,
      friendOutcome: null,
      friendRequestSent: false,
      partnerTyping: false,
      partnerLeft: false,
      replyTo: null,
      closedReason: null,
      /*
        Статус берётся у сервера, а не остаётся от прошлого разговора. Без
        этого в шапке сохранённого диалога висело «в сети» — то, что было
        правдой про случайного собеседника, из которого сюда пришли.
      */
      partnerOnline: chat.partner.isOnline,
      partnerAuthAway: false,
    }));
  },

  async removeFriend(chatId: string) {
    await api.removeFriend(chatId);
    set((state) => ({
      savedChats: state.savedChats.filter((saved) => saved.chatId !== chatId),
      ...(state.chat?.id === chatId
        ? { screen: 'saved' as FlowScreen, chat: null, messages: [], isFriendChat: false }
        : {}),
    }));
  },

  onFriendRequested(request: FriendRequestDto) {
    // Только для открытого сейчас диалога: предложение из другого чата человек
    // увидит, когда в него зайдёт.
    if (get().chat?.id === request.chatId) {
      set({ incomingRequest: request });
    }
  },

  onFriendAccepted(payload: FriendshipDto) {
    set((state) => ({
      isFriendChat: state.chat?.id === payload.chatId ? true : state.isFriendChat,
      friendshipJustMade: state.chat?.id === payload.chatId,
      /*
        Новость нужна тому, кто ждал ответа. Принявшему её сообщать незачем:
        он только что нажал «Принять» и всё про это знает.
      */
      friendOutcome:
        state.chat?.id === payload.chatId && state.friendRequestSent
          ? ('accepted' as const)
          : state.friendOutcome,
      friendRequestSent: false,
      incomingRequest: null,
    }));
    void get().loadSavedChats();
  },

  onFriendDeclined(chatId: string) {
    set((state) => ({
      incomingRequest: null,
      // Отказ приходит только отправителю, и молчать о нём нельзя: он ждёт
      // ответа и видит перед собой «предложение отправлено».
      friendOutcome:
        state.chat?.id === chatId && state.friendRequestSent
          ? ('declined' as const)
          : state.friendOutcome,
      friendRequestSent: false,
    }));
  },

  onFriendAlready(chatId: string) {
    set((state) => ({
      incomingRequest: null,
      friendRequestSent: false,
      friendOutcome: state.chat?.id === chatId ? ('already' as const) : state.friendOutcome,
    }));
  },

  clearFriendOutcome() {
    set({ friendOutcome: null });
  },

  onFriendRemoved(chatId: string) {
    set((state) => ({
      savedChats: state.savedChats.filter((saved) => saved.chatId !== chatId),
      ...(state.chat?.id === chatId
        ? { screen: 'saved' as FlowScreen, chat: null, messages: [], isFriendChat: false }
        : {}),
    }));
  },

  goToOnboarding() {
    set({
      screen: 'onboarding',
      chat: null,
      messages: [],
      search: idleSearch,
      replyTo: null,
      isFriendChat: false,
      friendshipJustMade: false,
      friendOutcome: null,
      friendRequestSent: false,
      incomingRequest: null,
    });
  },

  setError(message) {
    set({ lastError: message });
  },
}));
